import {
  ChartFormSubsectionPayload,
  ChartTrackingItem,
  findChartFormDifferences,
  useTrackChartInteractions,
} from "modules";
import React, { ReactNode, createContext, useContext } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";

import { extractApiErrorMessage, isSameUserId } from "utils";
import { genericErrors } from "messages";
import { useToast } from "@chakra-ui/react";
import { useActiveCharts } from "modules/charts-shared";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useAuditPermissions,
  useCommenterChartEditFamily,
} from "./Permissions";
import { useUserRoles } from "./UserRoles";
import { useAuditStatus } from "modules/audits/api";
import { AuditStatus } from "modules/audits/types";
import { getAuditStatusForChartType } from "modules/audits/utils";
import { USERID_CLAIMS } from "system-constants";

const INVALID_ANSWER_CODES = ["id", "userid"];
const USER_ANSWER_CODES = ["signedBy"];

type ChartProps = {
  encounterId: string;
  chartCode?: string;
  sectionId?: string;
  chartType: ChartTrackingItem["type"];
};

type ActiveSectionForm = {
  encounterId: string;
  sectionId: string;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  isReadOnly?: boolean;
  isDirty?: boolean;
  isSaving?: boolean;
};

type ChartTrackingContextType = {
  currentChartElements: Record<string, string>[] | null;
  updateChartElements: (newData: Record<string, string>[]) => void;
  appendChartElements: (newData: Record<string, string>[]) => void;
  isLoading: boolean;
  trackChartInteractions: (
    oldArray: ChartFormSubsectionPayload[],
    newArray: ChartFormSubsectionPayload[],
    chartProps: ChartProps
  ) => Promise<void>;
  isPastEncounter: boolean;
  setIsPastEncounter: (value: boolean) => void;
  /**
   * True when the chart is chart-`Completed` and no audit carve-out applies.
   *
   * Audit caveat: for an auditor in the audits flow this is deliberately `false`
   * both while the assigned auditor is editing AND for the brief window while the
   * audit-status query is still loading (assignment not yet known). During that
   * loading window it does NOT distinguish an assigned auditor from a not-assigned
   * one. Edit access no longer depends on it; `scope(<chartScope>).isEditable`
   * alone decides that. Consumers read it to decide which audit affordances a
   * chart-`Completed` chart still shows (audit-comment indicators and the audits
   * entry).
   *
   * Clinician carve-out: the assigned provider (for provider charts) or assigned
   * nurse (for nurse/triage charts) may edit a chart-`Completed` chart globally,
   * not only in the audits flow.
   *
   * Commenter carve-out: `isCommenterReviewing` is true when the current user is a
   * commenter and the chart-type audit status is `InReview`. Unlike the auditor
   * carve-outs, this is deliberately NOT gated on `fromAudits` because commenters
   * review in the regular chart flows, not the dedicated /audits route.
   */
  isCurrentChartCompleted: boolean;
  activeSectionForm: ActiveSectionForm | null;
  registerActiveSectionForm: (form: ActiveSectionForm) => void;
  unregisterActiveSectionForm: (sectionId: string) => void;
};

const ChartTrackingContext = createContext<
  ChartTrackingContextType | undefined
>(undefined);

const useChartTrackingContext = () => {
  const context = useContext(ChartTrackingContext);
  if (!context) {
    throw new Error(
      "useChartTrackingContext must be used within a ChartTrackingContextProvider"
    );
  }
  return context;
};

type ChartTrackingContextProviderProps = {
  children: ReactNode;
};

const ChartTrackingContextProvider: React.FC<
  ChartTrackingContextProviderProps
> = ({ children }) => {
  const location = useLocation();
  const { encounterId = "" } = useParams<{ encounterId: string }>();
  const [currentChartElements, setCurrentChartElements] = React.useState<
    Record<string, string>[] | null
  >(null);
  const currentChartElementsRef = React.useRef(currentChartElements);
  React.useEffect(() => {
    currentChartElementsRef.current = currentChartElements;
  }, [currentChartElements]);
  const [isPastEncounter, setIsPastEncounter] = React.useState<boolean>(false);

  const [activeSectionForm, setActiveSectionForm] =
    React.useState<ActiveSectionForm | null>(null);

  const registerActiveSectionForm = React.useCallback(
    (form: ActiveSectionForm) => {
      setActiveSectionForm(form);
    },
    []
  );

  const unregisterActiveSectionForm = React.useCallback((sectionId: string) => {
    setActiveSectionForm((current) =>
      current?.sectionId === sectionId ? null : current
    );
  }, []);

  const { data: activeCharts } = useActiveCharts(encounterId);

  const currentChartCode = React.useMemo(() => {
    const chartCodeMatch = location.pathname.match(
      /\/(?:triage|chart)\/([^/]+)/i
    );

    return chartCodeMatch?.[1] ?? "";
  }, [location.pathname]);

  const currentChartStatus = React.useMemo(() => {
    if (!currentChartCode) {
      return undefined;
    }

    return activeCharts?.data?.find(
      (chart) => chart.chartCode === currentChartCode
    )?.chartStatus;
  }, [activeCharts?.data, currentChartCode]);

  // An assigned auditor may edit a chart-Completed chart while auditing it, until
  // the audit for that chart type is itself Completed. Charts under audit are always
  // chart-Completed, so without this the completion locks make audits read-only.
  const { user } = useAuth0();
  const currentUserId = (user?.[USERID_CLAIMS] as string) || "";
  // Ungated role check on purpose: this feeds isCurrentChartCompleted, which
  // gates the audit affordances of a chart-`Completed` chart. Using the
  // lock-gated isAuditor here would make those gates depend on their own output.
  const { hasAuditorRole: isAuditor, isCommenter } = useAuditPermissions();
  const { setCommenterChartEditFamily } = useCommenterChartEditFamily();
  const { roles } = useUserRoles();
  const fromAudits = location.pathname.startsWith("/audits");
  const isProviderRole =
    roles.includes("Provider") || roles.includes("SupervisingProvider");
  const isNurseRole = roles.includes("Nurse");

  const currentChartType = React.useMemo<"Nurse" | "Provider" | "">(() => {
    // Detect the chart-type segment case-insensitively. The audits list
    // navigates with lowercase `/triage/` `/chart/` routes, but moving between a
    // patient's charts via the sidebar ChartPicker produces capitalized
    // `/Triage/` `/Chart/` segments. A case-sensitive check loses the chart type
    // on picker navigation, which misapplies the audit completion lock.
    const chartTypeSegment = location.pathname
      .match(/\/(triage|chart)\/[^/]+/i)?.[1]
      ?.toLowerCase();
    if (chartTypeSegment === "triage") return "Nurse";
    if (chartTypeSegment === "chart") return "Provider";
    return "";
  }, [location.pathname]);

  const isAuditStatusEnabled =
    (fromAudits ||
      isCommenter ||
      (currentChartStatus === "Completed" &&
        !!currentChartType &&
        (isProviderRole || isNurseRole))) &&
    !!encounterId;

  const { data: audit, isLoading: isAuditStatusLoading } = useAuditStatus(
    encounterId,
    {
      enabled: isAuditStatusEnabled,
    }
  );

  const assignedAuditorId =
    currentChartType === "Nurse"
      ? audit?.data?.nurseChartAuditorId
      : currentChartType === "Provider"
      ? audit?.data?.providerChartAuditorId
      : undefined;

  const assignedClinicianId =
    currentChartType === "Nurse"
      ? audit?.data?.nurseId
      : currentChartType === "Provider"
      ? audit?.data?.providerId
      : undefined;

  const isAuditTypeComplete =
    (currentChartType === "Nurse" &&
      audit?.data?.nurseChartAuditStatus === "Completed") ||
    (currentChartType === "Provider" &&
      audit?.data?.providerChartAuditStatus === "Completed");

  const isAssignedAuditorEditing =
    fromAudits &&
    isAuditor &&
    !!currentChartType &&
    isSameUserId(assignedAuditorId, currentUserId) &&
    !isAuditTypeComplete;

  // An assigned provider or nurse may edit a chart-Completed chart for the encounter
  // they are assigned to, regardless of whether the user arrived via audits.
  const isAssignedClinicianEditing =
    !!currentChartType && isSameUserId(assignedClinicianId, currentUserId);

  // While the audit-status query is still in flight we cannot yet know whether the
  // auditor is assigned, so this stays false for every auditor in the audits flow,
  // which avoids a brief affordance flash for the assigned auditor.
  //
  // Assumption: this intentionally suppresses the completion lock for a not-assigned
  // auditor too during the loading window. Edit access is unaffected because it comes
  // from scope().isEditable (Permissions.tsx), which stays read-only via the
  // auditChartReadOnly flag until assignment resolves. Consumers of
  // isCurrentChartCompleted only decide which audit affordances a chart-`Completed`
  // chart shows, so a not-assigned auditor briefly sees those affordances. See the doc
  // on ChartTrackingContextType.isCurrentChartCompleted.
  const isAuditorAwaitingStatus =
    fromAudits && isAuditor && !!currentChartType && isAuditStatusLoading;

  const currentChartAuditStatus = React.useMemo<AuditStatus | undefined>(
    () => getAuditStatusForChartType(audit?.data, currentChartType),
    [audit, currentChartType]
  );

  // A commenter may edit/review a chart-Completed chart while the audit for that
  // chart type is InReview (their turn).
  const isCommenterReviewing =
    isCommenter && currentChartAuditStatus === "InReview";

  const commenterEditFamily = React.useMemo<"Provider" | "Nurse" | null>(() => {
    if (!isCommenterReviewing) {
      return null;
    }
    if (
      currentChartType === "Provider" &&
      (roles.includes("Provider") || roles.includes("SupervisingProvider"))
    ) {
      return "Provider";
    }
    if (currentChartType === "Nurse" && roles.includes("Nurse")) {
      return "Nurse";
    }
    return null;
  }, [isCommenterReviewing, currentChartType, roles]);

  React.useEffect(() => {
    setCommenterChartEditFamily(commenterEditFamily);
    return () => {
      setCommenterChartEditFamily(null);
    };
  }, [commenterEditFamily, setCommenterChartEditFamily]);

  const isCurrentChartCompleted =
    currentChartStatus === "Completed" &&
    !isAssignedAuditorEditing &&
    !isAuditorAwaitingStatus &&
    !isCommenterReviewing &&
    !isAssignedClinicianEditing;

  const toast = useToast();

  const { mutateAsync: track, isLoading } = useTrackChartInteractions();

  const trackChartInteractions = async (
    oldArray: ChartFormSubsectionPayload[],
    newArray: ChartFormSubsectionPayload[],
    chartProps: ChartProps
  ) => {
    try {
      const {
        encounterId: encounterIdProp,
        chartType,
        chartCode,
        sectionId,
      } = chartProps;

      const trackingInfo: ChartTrackingItem[] = [];

      const checkElementType = (
        elementType: string,
        qCode: string | undefined
      ) => {
        if (!qCode) return "Text";
        return USER_ANSWER_CODES.includes(qCode.toLowerCase())
          ? "User"
          : elementType === "Date"
          ? "Date"
          : "Text";
      };

      findChartFormDifferences(oldArray, newArray)
        .filter(
          (x) => !INVALID_ANSWER_CODES.includes(x.qCode?.toLowerCase() || "")
        )
        .forEach((x) => {
          const chartElementInfo = currentChartElementsRef.current?.find(
            (el) => el.name?.split(".").pop() === x.qCode
          );

          const trackingItem = {
            chartCode,
            encounterId: encounterIdProp,
            sectionId,
            type: chartType,
            label: chartElementInfo?.label || chartElementInfo?.type,
            elementType: checkElementType(
              chartElementInfo?.type || "Text",
              x.qCode
            ),
            ...x,
          } as ChartTrackingItem;

          trackingInfo.push(trackingItem);
        });

      await track(trackingInfo);
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  const updateChartElements = (newData: Record<string, string>[]) => {
    setCurrentChartElements((prev) => {
      if (!prev || prev.length === 0) {
        return newData;
      }
      const existingNames = new Set(prev.map((el) => el.name));
      const merged = [...prev];
      newData.forEach((el) => {
        const name = el.name;
        if (!name || existingNames.has(name)) return;
        existingNames.add(name);
        merged.push(el);
      });
      return merged;
    });
  };

  const appendChartElements = (newData: Record<string, string>[]) => {
    setCurrentChartElements((prev) => {
      if (!prev || prev.length === 0) {
        return newData;
      }
      const nameToIndex = new Map(prev.map((el, i) => [el.name, i]));
      const merged = [...prev];
      newData.forEach((el) => {
        const name = el.name;
        if (!name) return;
        const existingIndex = nameToIndex.get(name);
        if (existingIndex !== undefined) {
          merged[existingIndex] = el;
        } else {
          nameToIndex.set(name, merged.length);
          merged.push(el);
        }
      });
      return merged;
    });
  };

  const contextValue: ChartTrackingContextType = {
    currentChartElements,
    updateChartElements,
    appendChartElements,
    trackChartInteractions,
    isLoading,
    isPastEncounter,
    setIsPastEncounter: (value: boolean) => setIsPastEncounter(value),
    isCurrentChartCompleted,
    activeSectionForm,
    registerActiveSectionForm,
    unregisterActiveSectionForm,
  };

  return (
    <ChartTrackingContext.Provider value={contextValue}>
      {children}
    </ChartTrackingContext.Provider>
  );
};

export {
  ChartTrackingContext,
  ChartTrackingContextProvider,
  useChartTrackingContext,
};
export type { ActiveSectionForm };
